import { createEffect } from 'effector'
import { check, LEVELS } from './level'

// save original console
globalThis.__console = globalThis.__console || globalThis.console

export class Logger implements Disposable {
  private allow: ReturnType<typeof check>
  private isSafari: boolean

  private detectSafari(): boolean {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent
    return /safari/i.test(ua) && !/chrome|chromium|android/i.test(ua)
  }

  public readonly prefix
  public readonly writer
  public readonly level?: number

  constructor(prefix = '', writer = globalThis.__console, level?: number) {
    this.prefix = prefix
    this.writer = writer
    this.level = level
    this.allow = check(prefix, level)
    this.isSafari = this.detectSafari()
  }

  new(prefix: string): Logger {
    return new Logger(this.prefix + '/' + prefix, this.writer)
  }

  // prettier-ignore
  prepend = (data: unknown[], level: string): unknown[] => {
    const prefixStyle = 'color:gray;font-weight:normal;font-size:0.8em;'
    const defaultStyle = 'color:inherit;font-weight:inherit;font-size:inherit;'
    const levelStyle = (color) => `color:${color};font-weight:100;font-size:0.8em;`
    const levelStyles = {
      trace: levelStyle('#9e9e9e'), // gray
      debug: levelStyle('#4a90e2'), // soft blue
      info:  levelStyle('#66bb6a'), // soft green
      log:   levelStyle('#d3d3d3'), // light gray
      warn:  levelStyle('#ffa726'), // amber
      error: levelStyle('#ef5350'), // red
    }
    if (data.length > 0) {
      if (
        typeof data[0] === 'string' &&
        (this.isSafari || data[0].includes('%'))
      ) {
        data[0] = `%c${this.prefix} %c(${level})%c ${data[0]}`
        data.splice(1, 0, prefixStyle, levelStyles[level], defaultStyle)
      } else {
        data.unshift(`%c${this.prefix} %c(${level})`, prefixStyle, levelStyles[level])
      }
    }
    return data
  }

  trace = (...data: unknown[]): void => {
    if (this.allow(LEVELS.trace)) {
      this.writer.debug(...this.prepend(data, 'trace')) // using `.debug` for trace level to avoid unnecessary stack trace generation of `.trace`
    }
  }

  debug = (...data: unknown[]): void => {
    if (this.allow(LEVELS.debug)) {
      this.writer.debug(...this.prepend(data, 'debug'))
    }
  }

  table = (...data: unknown[]): void => {
    if (this.allow(LEVELS.debug)) {
      this.writer.table(...data) // `.table` method doesn't support formatting
    }
  }

  info = (...data: unknown[]): void => {
    if (this.allow(LEVELS.info)) {
      this.writer.info(...this.prepend(data, 'info'))
    }
  }

  log = (...data: unknown[]): void => {
    if (this.allow(LEVELS.log)) {
      this.writer.log(...this.prepend(data, 'log'))
    }
  }

  dir = (...data: unknown[]): void => {
    if (this.allow(LEVELS.log)) {
      this.writer.dir(...data) // `.dir` method doesn't support formatting
    }
  }

  warn = (...data: unknown[]): void => {
    if (this.allow(LEVELS.warn)) {
      this.writer.warn(...this.prepend(data, 'warn'))
    }
  }

  error = (...data: unknown[]): void => {
    if (this.allow(LEVELS.error)) {
      this.writer.error(...this.prepend(data, 'error'))
    }
  }

  group = (...data: unknown[]): void => {
    this.writer.group(...this.prepend(data, 'debug'))
  }

  groupCollapsed = (...data: unknown[]): void => {
    this.writer.groupCollapsed(...this.prepend(data, 'debug'))
  }

  groupEnd = (): void => {
    this.writer.groupEnd()
  };

  [Symbol.dispose] = (): void => {
    this.groupEnd()
  }

  debugGroup = (...data: unknown[]): Logger | undefined => {
    if (this.allow(LEVELS.debug)) {
      this.groupCollapsed(...data)
      return new Logger('|', this.writer, LEVELS.trace)
    }
  }

  // effector extension
  traceFx = createEffect((data: unknown[]) => this.trace(...data))
  debugFx = createEffect((data: unknown[]) => this.debug(...data))
  tableFx = createEffect((data: unknown[]) => this.table(...data))
  infoFx = createEffect((data: unknown[]) => this.info(...data))
  logFx = createEffect((data: unknown[]) => this.log(...data))
  dirFx = createEffect((data: unknown[]) => this.dir(...data))
  warnFx = createEffect((data: unknown[]) => this.warn(...data))
  errorFx = createEffect((data: unknown[]) => this.error(...data))
}

// default application logger instance
export const logger = new Logger('tria')
